import { useState, useContext } from 'react';
import '../CompaniesPage/CompaniesPage.css';

import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { productRenderFields, industryDetails } from '../../constants.jsx';
import { renderFormRow, genericPOSTDatabaseCall, genericDELETEDatabaseCall} from '../../helperFunctions.jsx';

async function addProductToDatabase(product) {
    return genericPOSTDatabaseCall('/api/fb/addProduct', { product }, 'product');
}

async function saveProductToDatabase(product) {
    return genericPOSTDatabaseCall('/api/fb/saveProductInfo', { product }, 'product info');
}

async function deleteProductFromDatabase(productId, companyId) {
    return genericDELETEDatabaseCall('/api/fb/deleteProduct', { id: productId, company_id: companyId }, 'product');
}

export function ProductDetails({ product, setCompanySelected, messageApi, companySelected, modalView = false, useInvestorReadyFinancial = false }) {
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleInputChange = (event, product, key, field) => {
        let newValue = event.target.value;

        setCompanySelected(prev => ({
            ...prev,
            products: prev.products.map(prod => 
                prod.id === product.id ? { ...prod, [key]: newValue } : prod
            )
        }));
    }

    const handleInputBlur = (event, product, key, field) => {
        let newValue = event.target.value;

        if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
            messageApi.error(field.errorMessage);
            newValue = industryDetails[companySelected.industry][key] || field.default || ''; // Use default value if provided
        }

        if (newValue === '') {
            messageApi.error('This field cannot be empty');
            newValue = industryDetails[companySelected.industry][key] || field.default || ''; // Use default value if provided
        }

        setCompanySelected(prev => ({
            ...prev,
            products: prev.products.map(prod => 
                prod.id === product.id ? { ...prod, [key]: newValue } : prod
            )
        }));
    }

    const saveProduct = async (product) => {
        setSaving(true);
        messageApi.loading('Saving product info...', 0);
        try {
            await saveProductToDatabase(product);
            messageApi.destroy();
            messageApi.success('Product info saved successfully');
        } catch (error) {
            console.error('Error saving product info:', error);
            messageApi.destroy();
            messageApi.error('Failed to save product info');
        }
        setSaving(false);
    }

    const deleteProduct = async (productId, companyId) => {
        setDeleting(true);
        messageApi.loading('Deleting product...', 0);
        try {
            await deleteProductFromDatabase(productId, companyId);
            setCompanySelected(prev => ({
                ...prev,
                products: prev.products.filter(prod => prod.id !== productId)
            }));
            messageApi.destroy();
            messageApi.success('Product deleted successfully');
        } catch (error) {
            console.error('Error deleting product:', error);
            messageApi.destroy();
            messageApi.error('Failed to delete product');
        }
        setDeleting(false);
    }

    const offsetRenderFields = modalView ? 2 : 0;
    const financialMetrics = productRenderFields.slice(2, productRenderFields.length - 2 + offsetRenderFields).filter((item) => {
        if (useInvestorReadyFinancial){
            return item.key != 'market_capture'
        }
        return item.key != 'revenue_allocation'
    });

    return (
        <div key={product.id} className="entity-card">
            {/* Name and Description */}
            {productRenderFields.slice(0, 2).map(field => (
                renderFormRow(field, product, handleInputChange, true, handleInputBlur)
            ))}
            {/* Financial Metrics */}
            <div className="form-section-financial-metrics">
                {financialMetrics.map (field => (
                    renderFormRow(field, product, handleInputChange, true, handleInputBlur)
                ))}
            </div>
            <div className="flex-row bottom-actions">
                <div className="form-row actions-row">
                    <button type='button' onClick={() => deleteProduct(product.id, product.company_id)} disabled={deleting} className="delete-button">Delete Product</button>
                </div>
                <div className="form-row actions-row">
                    <button type='button' onClick={() => saveProduct(product)} disabled={saving} className="save-button">Save Product</button>
                </div>
            </div>
        </div>
    )
}

function ProductsPage({showNavigationControls = true}) {
    const { setPageNum, companySelected, setCompanySelected, messageApi } = useContext(FinancialBuilderContext);
    const [newProductName, setNewProductName] = useState("");

    const addNewProduct = async () => {
        if (!newProductName) {
            messageApi.error('Please enter a product name');
            return;
        }
        messageApi.loading('Adding product...', 0);
        try {
            const newProduct = await addProductToDatabase({
                name: newProductName,
                company_id: companySelected.id,
                growth_rate: industryDetails[companySelected.industry]['default_growth_rate'],
                growth_adjustment: industryDetails[companySelected.industry]['default_growth_adjustment']
            });
            setCompanySelected(prev => ({
                ...prev,
                products: [...prev.products, newProduct]
            }));
            setNewProductName(''); // Clear input field
            messageApi.destroy();
            messageApi.success('Product added successfully');
        } catch (error) {
            console.error('Error adding product:', error);
            messageApi.destroy();
            messageApi.error('Failed to add product');
        }
    }

    return (
        <div className="financial-builder-container entity-industry-container">
            <h2>{companySelected.name}- Products ({companySelected.products.length})</h2>
            <div className="product-list">
                <h3>Existing Products</h3>
                <div>
                    {companySelected.products.map(product => (
                        <ProductDetails modalView={!showNavigationControls} key={product.id} product={product} setCompanySelected={setCompanySelected} messageApi={messageApi} companySelected={companySelected} />
                    ))}
                </div>
            </div>
            <div className="add-entity-controls form-section">
                <div className="form-row">
                    <label htmlFor="new_product_name">Add New Product</label>
                    <input type="text" className="form-input" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} placeholder="Enter product name" id="new_product_name" />
                </div>
                <button className="add-button" onClick={addNewProduct}>ADD Product</button>
            </div>
            {!showNavigationControls ? <></>: (
            <div className="navigation-button-container">
                <button type="button" onClick={() => setPageNum(1)} className="next-button">Back</button>
                <button type="button" disabled={companySelected.products.length < 1} onClick={() => setPageNum(3)} className="next-button">Next</button>
            </div>
            )}
        </div>
    );
}

export default ProductsPage;